import { Sparkle } from '@phosphor-icons/react';

import { RowActions } from '../common/RowActions';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { categoryLabel, categoryMeta } from './assetCategories';

function AccountBadges({ account }) {
  const archived = !account.is_active;

  return (
    <>
      {account.is_emergency_fund_part ? (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-ds-full bg-sage-tint px-2 py-0.5 type-chip font-semibold text-sage-on-tint">
          <Sparkle className="h-2.5 w-2.5" aria-hidden />
          {pt.accounts.badges.emergencyFund}
        </span>
      ) : null}
      {archived ? (
        <span className="shrink-0 rounded-ds-full bg-stone-200 px-2 py-0.5 type-chip font-semibold text-muted-foreground dark:bg-stone-700">
          {pt.accounts.badges.archived}
        </span>
      ) : null}
    </>
  );
}

function accountDetailsLine(account) {
  const parts = [categoryLabel(account.category), account.currency];
  if (account.ticker) parts.push(account.ticker);
  if (account.quantity) parts.push(String(account.quantity));
  return parts.join(' · ');
}

export function AccountRowActions({ account, onEdit, onArchive, onRestore }) {
  return (
    <RowActions
      item={account}
      onEdit={onEdit}
      onArchive={onArchive}
      onRestore={onRestore}
      editLabel={pt.accounts.actions.edit}
      archiveLabel={pt.accounts.actions.archive}
      restoreLabel={pt.accounts.actions.restore}
    />
  );
}

/**
 * `.pen` → `ds/row/composition-item` (variante arquivada): mesmo chip
 * uniforme sage do `AccountAssetRow`, mas com badges (fundo de emergência /
 * arquivado) em vez da etiqueta de horizonte, subtítulo com os detalhes da
 * conta, sem valor, e acções editar/reactivar. Sem cartão/borda próprios —
 * vive dentro da secção "Arquivados".
 */
export function AccountRow({
  account,
  actions,
  onEdit,
  onArchive,
  onRestore,
  className,
  dimmed = false,
}) {
  const meta = categoryMeta(account.category);
  const Icon = meta.icon;

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-space-3',
        dimmed && 'opacity-90',
        className,
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-md bg-sage-tint text-sage-on-tint">
        <Icon size={16} aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <p className="min-w-0 truncate type-body font-semibold text-foreground" title={account.name}>
            {account.name}
          </p>
          <AccountBadges account={account} />
        </div>
        <p
          className="truncate type-chip font-normal text-muted-foreground"
          title={accountDetailsLine(account)}
        >
          {accountDetailsLine(account)}
          {account.notes ? (
            <>
              <span className="text-stone-300"> · </span>
              <span className="text-muted-foreground/70">{account.notes}</span>
            </>
          ) : null}
        </p>
      </div>

      {actions ? (
        <div className="flex shrink-0 items-center gap-0.5">{actions}</div>
      ) : onEdit && (onArchive || onRestore) ? (
        <div className="flex shrink-0 items-center gap-0.5">
          <AccountRowActions account={account} onEdit={onEdit} onArchive={onArchive} onRestore={onRestore} />
        </div>
      ) : null}
    </div>
  );
}
